// Провод трекера v3 (docs/api-tracker-v3.md, server/agentbox/notes/tracker_board.py).

import { trackerApi } from '../../api'

export type ColumnId = 'inbox' | 'work' | 'wait' | 'done'
export type TrackerViewMode = 'board' | 'list' | 'archive'

export interface TrackerLabel {
  id: string
  title: string
  /** имя токена без «--»: term-link, warn, ok, agent, rec, soft */
  color: string
  icon: 'plane' | 'person' | 'tag' | string
  builtin: boolean
}

export interface TrackerBoard {
  id: string
  title: string
  open: number
  closed: number
}

export interface TrackerConfig {
  boards: TrackerBoard[]
  labels: TrackerLabel[]
  columns: { id: ColumnId; title: string }[]
}

export interface TrackerItem {
  path: string
  kind: 'task' | 'message'
  title: string
  description: string
  board: string
  column: ColumnId
  label: string
  order: number
  closed: boolean
  closedAt: number | null
  due: number | null
  late: boolean
  created: number
  updated: number
  source: string
  link: string | null
  contact: string | null
}

export interface CreateItem {
  title: string
  description?: string
  board?: string
  column?: ColumnId
  label?: string
  /** срок, ISO-дата «2026-09-25» */
  due?: string
}

export type PatchItem = Partial<Pick<TrackerItem, 'title' | 'description' | 'board' | 'column' | 'label' | 'order' | 'closed'>> & {
  path: string
  /** ISO-дата; null — снять срок */
  due?: string | null
}

const call = trackerApi.v3
const enc = encodeURIComponent

export const trackerV3 = {
  config: () => call<TrackerConfig>('/config'),
  items: () => call<TrackerItem[]>('/items'),
  create: (body: CreateItem) => call<TrackerItem>('/items', 'POST', body),
  patch: (body: PatchItem) => call<TrackerItem>('/items', 'PATCH', body),
  remove: (path: string) => call<void>(`/items?path=${enc(path)}`, 'DELETE'),
  addBoard: (title: string) => call<TrackerBoard>('/boards', 'POST', { title }),
  renameBoard: (id: string, title: string) => call<TrackerBoard>(`/boards/${enc(id)}`, 'PATCH', { title }),
  dropBoard: (id: string) => call<TrackerConfig>(`/boards/${enc(id)}`, 'DELETE'),
  addLabel: (title: string) => call<TrackerLabel>('/labels', 'POST', { title }),
  dropLabel: (id: string) => call<TrackerConfig>(`/labels/${enc(id)}`, 'DELETE'),
}

/** «term-link» → var(--term-link); неизвестный статус — --dim. */
export function labelColor(label: TrackerLabel | undefined): string {
  return label ? `var(--${label.color})` : 'var(--dim)'
}

/** Возраст задачи: «сейчас», «12 мин», «3 ч», «2 д». */
export function ageText(created: number, now = Date.now() / 1000): string {
  const s = Math.max(0, now - created)
  if (s < 60) return 'сейчас'
  if (s < 3600) return `${Math.floor(s / 60)} мин`
  if (s < 86400) return `${Math.floor(s / 3600)} ч`
  return `${Math.floor(s / 86400)} д`
}

/** «закрыта сегодня / вчера / 3 д назад / 12 сент.». */
export function closedText(at: number | null, now = new Date()): string {
  if (at == null) return 'закрыта'
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const when = new Date(at * 1000)
  const diff = Math.round((day(now) - day(when)) / 86400000)
  if (diff <= 0) return 'закрыта сегодня'
  if (diff === 1) return 'закрыта вчера'
  if (diff < 7) return `закрыта ${diff} д назад`
  return `закрыта ${when.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`
}

/** Срок (unix-секунды, конец дня) → «до 25 сент.». */
export function dueText(due: number): string {
  return `до ${new Date(due * 1000).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`
}

/** Срок → значение <input type="date"> (локальная дата). */
export function dueInput(due: number | null): string {
  if (due == null) return ''
  const d = new Date(due * 1000)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
